import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { useTranslation } from 'react-i18next';
import { useUpdate } from '@services/staff/benchUsers/update';
import { BenchUser } from '@root/globalTypes';

export function useBenchUserUpdateForm({
  user,
  successHandler,
}: {
  user: BenchUser;
  successHandler: (data: BenchUser) => void;
}) {
  const { t } = useTranslation('app');

  const schema = z.object({
    username: z
      .string()
      .trim()
      .min(1, { message: t('form.username.required') }),
    password: z
      .string()
      .trim()
      .min(8, { message: t('form.password.complexity') })
      .or(z.literal('')),
    expiresAt: z.date(),
  });

  const defaultValues: z.infer<typeof schema> = {
    username: user.username,
    password: '',
    expiresAt: new Date(user.expiresAt),
  };

  const form = useForm<z.infer<typeof schema>>({
    resolver: zodResolver(schema),
    defaultValues,
  });

  const { mutate, isPending, error } = useUpdate({
    callback: successHandler,
  });

  const onSubmit = async (values: z.infer<typeof schema>) => {
    mutate({
      id: user.id,
      username: values.username,
      expiresAt: values.expiresAt.toISOString(),
      ...(values.password ? { password: values.password } : {}),
    });
  };

  return {
    form,
    onSubmit,
    isPending,
    error,
  };
}
